import React, { useState, useCallback } from 'react';
import { X, RotateCcw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrackingInputPlayer = ({ config, taskTitle, onComplete, onClose }) => {
  const { fields, color, api_route, input_type } = config;
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [values, setValues] = useState(() => {
    const init = {};
    fields.forEach(f => { init[f.name] = ''; });
    return init;
  });

  const allFilled = fields.every(f => values[f.name] !== '');

  const handleChange = (name, val) => { setError(''); setValues(prev => ({ ...prev, [name]: val })); };

  const handleSubmit = async () => {
    for (const f of fields) {
      const num = parseFloat(values[f.name]);
      if (isNaN(num) || num < f.min || num > f.max) {
        setError(`${f.label} değeri ${f.min}-${f.max} arasında olmalı`);
        return;
      }
    }
    setSubmitting(true); setError('');
    try {
      const body = {};
      fields.forEach(f => { body[f.name] = parseFloat(values[f.name]); });
      await axios.post(`${BACKEND_URL}${api_route}`, body);
      setFinished(true);
    } catch { setError('Değer kaydedilirken bir hata oluştu. Tekrar deneyin.'); }
    finally { setSubmitting(false); }
  };

  const handleReset = () => {
    const init = {};
    fields.forEach(f => { init[f.name] = ''; });
    setValues(init); setStarted(false); setFinished(false); setError('');
  };

  const handleFinish = useCallback(() => {
    const body = {};
    fields.forEach(f => { body[f.name] = parseFloat(values[f.name]); });
    onComplete({ started: true, input_type, values_submitted: body, task_type: 'tracking_input' });
  }, [values, fields, input_type, onComplete]);

  const typeLabels = { blood_pressure: 'Tansiyon', weight: 'Kilo', heart_rate: 'Nabız', glucose: 'Kan Şekeri' };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 select-none" data-testid="tracking-player">
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition">
          <X className="w-5 h-5 text-white/40" />
        </button>
        <span className="text-white/30 text-xs font-medium tracking-wider uppercase">{taskTitle}</span>
        <div className="w-9" />
      </div>

      {!started && !finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-24 h-24 rounded-2xl flex items-center justify-center" style={{
            background: `${color}15`, border: `1px solid ${color}25`, boxShadow: `0 0 40px ${color}15`,
          }}>
            <svg className="w-12 h-12" style={{ color }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M4 4h16v16H4V4z" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 12h16M12 4v16" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-center space-y-3">
            <h2 className="text-white text-2xl font-bold">{taskTitle}</h2>
            <p className="text-white/40 text-sm">{typeLabels[input_type] || 'Değer'} değerini gir ve kaydet</p>
          </div>
          <button onClick={() => setStarted(true)}
            className="px-10 py-4 rounded-2xl text-base font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)`, boxShadow: `0 12px 40px ${color}40` }}
            data-testid="tracking-start">
            Değerleri Gir
          </button>
        </div>
      ) : finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.15)', boxShadow: '0 0 60px rgba(52,211,153,0.2)' }}>
            <svg className="w-14 h-14 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-white text-2xl font-bold">Kaydedildi!</h2>
            <div className="flex items-center justify-center gap-3 mt-2">
              {fields.map(f => (
                <div key={f.name} className="px-4 py-2 rounded-xl" style={{ background: `${color}10`, border: `1px solid ${color}20` }}>
                  <span className="text-white/40 text-xs block">{f.label}</span>
                  <span className="text-lg font-bold" style={{ color }}>{values[f.name]} <span className="text-xs text-white/30">{f.unit}</span></span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleReset} className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 transition" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <RotateCcw className="w-4 h-4" /> Düzelt
            </button>
            <button onClick={handleFinish} className="px-8 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #34D399, #34D399BB)', boxShadow: '0 8px 30px rgba(52,211,153,0.3)' }} data-testid="tracking-finish">
              Devam Et
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center w-full max-w-sm gap-6 mt-4">
          <h3 className="text-white/50 text-xs uppercase tracking-widest">{typeLabels[input_type] || 'Değer'} Girişi</h3>

          <div className="w-full space-y-4">
            {fields.map(f => (
              <div key={f.name} className="w-full">
                <label className="text-white/40 text-xs font-medium mb-2 block">{f.label} ({f.unit})</label>
                <div className="relative">
                  <input type="number" inputMode="decimal" value={values[f.name]}
                    onChange={e => handleChange(f.name, e.target.value)} placeholder={f.placeholder}
                    className="w-full py-4 px-5 rounded-xl text-2xl font-bold text-white text-center outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: `2px solid ${values[f.name] ? `${color}40` : 'rgba(255,255,255,0.08)'}`,
                      boxShadow: values[f.name] ? `0 0 20px ${color}10` : 'none',
                    }} data-testid={`tracking-input-${f.name}`} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 text-sm">{f.unit}</span>
                </div>
                <p className="text-white/15 text-[10px] mt-1 text-center">{f.min} — {f.max} {f.unit}</p>
              </div>
            ))}
          </div>

          {error && <p className="text-red-400 text-xs text-center">{error}</p>}

          <button onClick={handleSubmit} disabled={!allFilled || submitting}
            className="w-full py-4 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.01] disabled:opacity-30"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)`, boxShadow: `0 8px 30px ${color}30` }}
            data-testid="tracking-submit">
            {submitting ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin mx-auto" /> : 'Kaydet'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TrackingInputPlayer;
